<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Triangle from '@/assets/images/icons/triangle.svg';
import { TransitionName } from "~/enums/transition.enums";

const props = defineProps<{
  attachedTo: string;
  title: string;
  message: string;
}>();

const showHint = defineModel<boolean>();

const position = ref({
  y: 0,
  x: 0,
});

const updatePosition = () => {
  if (props.attachedTo) {
    const element = document.querySelector(props.attachedTo) as HTMLElement;

    if (element) {
      const rect = element.getBoundingClientRect();
      position.value.y = rect.top + window.scrollY + rect.height;
      position.value.x = rect.left + window.scrollX - 320 + rect.width / 2;
    }
  }
};

const findElement = () => {
  if (props.attachedTo) {
    const element = document.querySelector(props.attachedTo) as HTMLElement;

    if (element) {
      element.focus();
      updatePosition();
      return;
    }
  }

  nextTick(() => {
    findElement();
  });
};

const startWatching = () => {
  if (!showHint.value) {
    return;
  }
  findElement();
};

watch(() => showHint.value, () => {
  startWatching();
});

onMounted(() => {
  startWatching();

  window.addEventListener('resize', updatePosition);
  window.addEventListener('scroll', updatePosition);

  if (props.attachedTo) {
    const element = document.querySelector(props.attachedTo) as HTMLElement;
    if (element) {
      const observer = new MutationObserver(updatePosition);
      observer.observe(element, { attributes: true, childList: true, subtree: true });

      onBeforeUnmount(() => {
        observer.disconnect();
      });
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePosition);
  window.removeEventListener('scroll', updatePosition);
});
</script>

<template>
  <Transition
    mode="out-in"
    name="fade"
  >
    <div
      v-if="showHint"
      class="fixed w-full h-full top-0 left-0 bg-black/10 z-[999]"
      @click="showHint = false"
    />
  </Transition>

  <Transition
    mode="out-in"
    :name="TransitionName.SlideLeft"
  >
    <div
      v-if="showHint"
      class="fixed z-[1000] flex flex-col items-end w-[340px]"
      :style="`top: ${position.y}px; left: ${position.x}px;`"
    >
      <Triangle class="mr-2.5 -mb-[1px]" />

      <div class="bg-white p-3.5 shadow-medmastery rounded-md hint-border">
        <div class="flex justify-between">
          <div class="text-16 font-bold text-neutral-100">
            {{title}}
          </div>

          <Button
            class="bg-transparent border-none text-neutral-60 p-2 w-3.5 h-3.5"
            @click="showHint = false"
          >
            <template #icon>
              <FontAwesomeIcon icon="fa-xmark" />
            </template>
          </Button>
        </div>

        <div class="text-14 text-neutral-80 mt-2.5">
          {{message}}
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.hint-border {
  border: 1px solid #E3E8EF;
}
</style>